'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '@/context';
import { trackException } from '@/lib/appInsights';
import { isChunkLoadError, isChunkLoadErrorSplashEnabled } from '@/utils/chunkLoadError';
import ErrorIcon from '@/assets/images/ideation-phase-images/ErrorIcon.svg';

/** Body copy for generic / non-chunk failures */
export const APP_ERROR_SPLASH_DEFAULT_DESCRIPTION =
  'An unexpected error occurred. You can try again, or refresh the page if the problem continues.';

/** Body copy when the failure is a chunk / deployment mismatch */
export const APP_ERROR_SPLASH_CHUNK_DESCRIPTION =
  'The app could not load a required update. This often happens after a new version is published. Refresh the page to load the latest version.';

export interface ChunkLoadErrorSplashProps {
  /** Full page reload by default. Omit when rendering from a Server Component. */
  onRetry?: () => void;
  /** Explanatory text under the title */
  description?: string;
}

const reloadPage = () => {
  window.location.reload();
};

export const ChunkLoadErrorSplash: React.FC<ChunkLoadErrorSplashProps> = ({
  onRetry = reloadPage,
  description = APP_ERROR_SPLASH_DEFAULT_DESCRIPTION,
}) => {
  const { darkMode } = useTheme();

  return (
    <Box
      role="alert"
      aria-live="assertive"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: (theme) => theme.zIndex.modal + 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: darkMode ? '#1a1a1a' : '#ffffff',
        p: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 2,
          maxWidth: 520,
        }}
      >
        <Box sx={{ width: 64, height: 64, position: 'relative' }}>
          <Image src={ErrorIcon} alt="" width={64} height={64} />
        </Box>
        <Typography
          component="h1"
          sx={{
            fontFamily: '"Cerebri Sans", sans-serif',
            fontSize: { xs: '1.375rem', sm: '1.75rem' },
            fontWeight: 600,
            color: darkMode ? '#f8f8f2' : '#2c2c2c',
          }}
        >
          Something went wrong
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Cerebri Sans", sans-serif',
            fontSize: { xs: '0.9375rem', sm: '1rem' },
            fontWeight: 500,
            lineHeight: 1.6,
            color: darkMode ? 'rgba(248,248,242,0.85)' : 'rgba(44,44,44,0.85)',
          }}
        >
          {description}
        </Typography>
        <Button
          variant="contained"
          onClick={onRetry}
          sx={{
            mt: 1,
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: '#c81e24',
            '&:hover': { bgcolor: '#a01820' },
          }}
        >
          Try again
        </Button>
      </Box>
    </Box>
  );
};

/**
 * Chunk load failures that surface as unhandled promise rejections (not always caught by error boundaries).
 * Other unhandled rejections are left to the app; the boundary splash covers general render errors.
 */
export function ChunkLoadErrorGlobalHandler() {
  const [rejection, setRejection] = useState<unknown>(null);

  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      if (!isChunkLoadError(event.reason)) return;
      if (!isChunkLoadErrorSplashEnabled()) return;
      const err =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason));
      trackException(err);
      event.preventDefault();
      setRejection(event.reason);
    };

    window.addEventListener('unhandledrejection', handleRejection);
    return () => window.removeEventListener('unhandledrejection', handleRejection);
  }, []);

  if (rejection == null) return null;

  return <ChunkLoadErrorSplash description={APP_ERROR_SPLASH_CHUNK_DESCRIPTION} />;
}
